import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { supabase, mapStatus, TMS_COL } from '../lib/supabase';

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [drag, setDrag] = useState(false);
  const [newEntities, setNewEntities] = useState([]); // draft entities after import
  const [setupModal, setSetupModal] = useState(null); // entity being set up
  const inputRef = useRef();

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setResult(null);
    setNewEntities([]);
    Papa.parse(f, {
      complete: (res) => {
        const rows = res.data.filter(r => r.some(c => c));
        setPreview({ rows, total: rows.length - 1 });
      },
      skipEmptyLines: true,
    });
  }

  async function handleImport() {
    if (!preview) return;
    setLoading(true);
    setResult(null);

    try {
      const rows = preview.rows.slice(1);

      // Fetch all entities (all statuses)
      const { data: entities } = await supabase.from('entities').select('id, tms_client_name, status');
      const entityMap = {};
      (entities || []).forEach(e => {
        entityMap[e.tms_client_name.toLowerCase().trim()] = e;
      });

      // Find unique client names in import not yet in DB and not dismissed
      const importedClients = [...new Set(
        rows.map(r => (r[TMS_COL.CLIENT] || '').trim()).filter(Boolean)
      )];

      const unknownClients = importedClients.filter(c => {
        const existing = entityMap[c.toLowerCase()];
        return !existing; // not in DB at all
      });

      // Auto-create draft entities for unknown clients
      let createdDrafts = [];
      if (unknownClients.length > 0) {
        const drafts = unknownClients.map(c => ({
          name: toTitleCase(c),
          tms_client_name: c,
          status: 'draft',
          cadence: 'EOD',
          show_open_loads: false,
        }));
        const { data: created } = await supabase
          .from('entities')
          .insert(drafts)
          .select();
        createdDrafts = created || [];

        // Add new drafts to entityMap
        createdDrafts.forEach(e => {
          entityMap[e.tms_client_name.toLowerCase().trim()] = e;
        });
      }

      // Build loads
      const loads = rows.map(row => {
        const status = mapStatus(row[TMS_COL.STATUS]);
        const clientName = (row[TMS_COL.CLIENT] || '').trim();
        const entity = entityMap[clientName.toLowerCase()];
        const entityId = entity?.id || null;
        const pickupRaw = row[TMS_COL.START];
        const deliveryRaw = row[TMS_COL.END];

        return {
          load_num: (row[TMS_COL.LOAD_NUM] || '').trim(),
          tms_client_name: clientName,
          entity_id: entityId,
          status,
          origin_city: (row[TMS_COL.ORIG_CITY] || '').trim(),
          origin_state: (row[TMS_COL.ORIG_STATE] || '').trim(),
          dest_city: (row[TMS_COL.DEST_CITY] || '').trim(),
          dest_state: (row[TMS_COL.DEST_STATE] || '').trim(),
          equipment: (row[TMS_COL.EQUIPMENT] || '').trim(),
          pickup_dt: pickupRaw ? new Date(pickupRaw).toISOString() : null,
          delivery_dt: deliveryRaw ? new Date(deliveryRaw).toISOString() : null,
          delivered_at: status === 'Delivered' ? (deliveryRaw ? new Date(deliveryRaw).toISOString() : new Date().toISOString()) : null,
          last_imported_at: new Date().toISOString(),
        };
      }).filter(l => l.load_num);

      const { error } = await supabase.from('loads').upsert(loads, { onConflict: 'load_num' });
      if (error) throw error;

      setResult({ success: true, count: loads.length });
      setNewEntities(createdDrafts);
    } catch (err) {
      setResult({ success: false, error: err.message });
    }

    setLoading(false);
  }

  async function activateEntity(entity, form) {
    await supabase.from('entities').update({
      name: form.name,
      cadence: form.cadence,
      show_open_loads: form.show_open_loads,
      status: 'active',
    }).eq('id', entity.id);

    // Add contacts
    if (form.emails.length > 0) {
      const contacts = form.emails
        .filter(e => e.email)
        .map(e => ({ entity_id: entity.id, name: e.name, email: e.email }));
      if (contacts.length > 0) {
        await supabase.from('contacts').insert(contacts);
      }
    }

    setNewEntities(prev => prev.filter(e => e.id !== entity.id));
    setSetupModal(null);
  }

  async function dismissEntity(entity) {
    await supabase.from('entities').update({ status: 'dismissed' }).eq('id', entity.id);
    setNewEntities(prev => prev.filter(e => e.id !== entity.id));
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Import TMS Export</h1>
          <p className="page-subtitle">Upload your CSV export to refresh active loads</p>
        </div>
      </div>

      <div className="card">
        <div
          className={`upload-zone${drag ? ' drag-over' : ''}`}
          onClick={() => inputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <div className="upload-icon">📂</div>
          {file ? (
            <>
              <p><strong>{file.name}</strong></p>
              <p className="text-muted" style={{marginTop:4}}>{preview ? `${preview.total} loads detected` : 'Parsing...'}</p>
            </>
          ) : (
            <>
              <p><strong>Drop your TMS CSV here</strong> or click to browse</p>
              <p className="text-muted" style={{marginTop:4}}>CSV files only</p>
            </>
          )}
          <input ref={inputRef} type="file" accept=".csv" style={{display:'none'}} onChange={e => handleFile(e.target.files[0])} />
        </div>

        {result && (
          <div className={`alert alert-${result.success ? 'success' : 'error'}`} style={{marginTop:16}}>
            {result.success ? `✓ Import complete — ${result.count} loads loaded.` : `Error: ${result.error}`}
          </div>
        )}

        {preview && !result && (
          <div style={{marginTop:16}}>
            <div className="alert alert-info">
              Ready to import <strong>{preview.total} loads</strong> from <strong>{file.name}</strong>.
            </div>
            <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => { setFile(null); setPreview(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleImport} disabled={loading}>
                {loading ? <><span className="spinner" style={{width:14,height:14}} /> Importing...</> : '⬆ Import Loads'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New customers found */}
      {newEntities.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">🆕 {newEntities.length} New Customer{newEntities.length > 1 ? 's' : ''} Found</span>
            <span className="text-muted text-sm">Set up to include in reports, or dismiss</span>
          </div>
          <table style={{width:'100%', fontSize:13}}>
            <thead>
              <tr style={{background:'#f8f9fc'}}>
                <th style={{padding:'8px 12px', textAlign:'left', color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>TMS CLIENT NAME</th>
                <th style={{padding:'8px 12px', textAlign:'left', color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>LOADS IN IMPORT</th>
                <th style={{padding:'8px 12px'}}></th>
              </tr>
            </thead>
            <tbody>
              {newEntities.map(entity => {
                const loadCount = (preview?.rows.slice(1) || []).filter(r =>
                  (r[TMS_COL.CLIENT] || '').trim() === entity.tms_client_name
                ).length;
                return (
                  <tr key={entity.id} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'10px 12px', fontFamily:'var(--font-mono)', fontWeight:500}}>{entity.tms_client_name}</td>
                    <td style={{padding:'10px 12px', color:'var(--text-secondary)'}}>{loadCount} load{loadCount !== 1 ? 's' : ''}</td>
                    <td style={{padding:'10px 12px', textAlign:'right', display:'flex', gap:8, justifyContent:'flex-end'}}>
                      <button className="btn btn-danger btn-sm" onClick={() => dismissEntity(entity)}>Dismiss</button>
                      <button className="btn btn-primary btn-sm" onClick={() => setSetupModal(entity)}>Set Up →</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <div className="card-header"><span className="card-title">Import Notes</span></div>
        <ul style={{color:'var(--text-secondary)', fontSize:13, lineHeight:2, paddingLeft:18}}>
          <li>New customers are auto-created as drafts — set them up or dismiss after import</li>
          <li>Dismissed customers won't prompt again but can be reactivated in the Customers tab</li>
          <li>Existing Description, Unit #, PO #, Priority, and Exclude flags are preserved on re-import</li>
          <li>Status mapping: Released → Open, Accepted → Dispatched, InTransit → In Transit</li>
        </ul>
      </div>

      {setupModal && (
        <SetupModal
          entity={setupModal}
          onActivate={(form) => activateEntity(setupModal, form)}
          onClose={() => setSetupModal(null)}
        />
      )}
    </div>
  );
}

function SetupModal({ entity, onActivate, onClose }) {
  const [form, setForm] = useState({
    name: entity.name || toTitleCase(entity.tms_client_name),
    cadence: 'EOD',
    show_open_loads: false,
    emails: [{ name: '', email: '' }],
  });

  function addContact() {
    setForm(p => ({ ...p, emails: [...p.emails, { name: '', email: '' }] }));
  }

  function updateContact(i, field, val) {
    const emails = [...form.emails];
    emails[i][field] = val;
    setForm(p => ({ ...p, emails }));
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2 className="modal-title">Set Up Customer</h2>
        <div style={{marginBottom:16, padding:'10px 14px', background:'#f4f5fb', borderRadius:7, fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-secondary)'}}>
          TMS: {entity.tms_client_name}
        </div>

        <div className="form-group">
          <label className="form-label">Display Name</label>
          <input className="form-input" value={form.name} onChange={e => setForm(p=>({...p, name:e.target.value}))} />
        </div>

        <div className="form-group">
          <label className="form-label">Report Cadence</label>
          <select className="form-input form-select" value={form.cadence} onChange={e => setForm(p=>({...p, cadence:e.target.value}))}>
            <option value="Twice Daily">Twice Daily (10am & 4pm)</option>
            <option value="EOD">End of Day</option>
          </select>
        </div>

        <div className="form-group">
          <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13}}>
            <input type="checkbox" checked={form.show_open_loads} onChange={e => setForm(p=>({...p, show_open_loads:e.target.checked}))} />
            Show Open (uncovered) loads to this customer
          </label>
        </div>

        <div className="form-group">
          <label className="form-label">Contacts</label>
          {form.emails.map((c, i) => (
            <div key={i} style={{display:'flex', gap:8, marginBottom:8}}>
              <input className="form-input" placeholder="Name" value={c.name} onChange={e => updateContact(i, 'name', e.target.value)} style={{flex:1}} />
              <input className="form-input" placeholder="Email" type="email" value={c.email} onChange={e => updateContact(i, 'email', e.target.value)} style={{flex:2}} />
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={addContact}>+ Add Contact</button>
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onActivate(form)}>Activate Customer</button>
        </div>
      </div>
    </div>
  );
}

function toTitleCase(str) {
  return (str || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}
